import { TransformType } from '@mr-yum/cdn-image'
import { BlockedFilledIcon, ChevronRightIcon } from '@mr-yum/frontend-ui'
import cn from 'classnames'
import React from 'react'
import { FormattedMessage } from 'react-intl'

import { Sheet } from '@/components/Common/Sheet'
import { FlexibleWaitTimesBadge } from '@/components/FlexibleWaitTimes/FlexibleWaitTimesLabel'
import { ResponsiveModal } from '@/components/ResponsiveModal'
import { Image } from '@/components/Shared/Image'
import { DefaultImage } from '@/components/Venue/Categories/DefaultImage'
import { GuestImage, MenuQuery } from '@/gql/graphql'

import { MenuBadge } from './MenuBadge'

interface CategorySheetProps {
  categorySheetOpen: boolean
  setCategorySheetOpen: React.Dispatch<React.SetStateAction<boolean>>
  menuCategories: MenuQuery['guestMenuCategories']
  activeCategory: MenuQuery['guestMenuCategory']
  handleCategorySelect: (categorySlug: string) => () => void
}

interface CategoryItem {
  slug: string
  name: string
  image?: GuestImage
  isWaitTimeActive: boolean
  waitTimeEstimationInMinutes?: number | null
  isClosed: boolean
}

const CategorySheetItem = ({
  category,
  isActive,
  handleCategorySelect,
}: {
  category: CategoryItem
  isActive: boolean
  handleCategorySelect: (categorySlug: string) => () => void
}) => {
  return (
    <button
      className={cn(
        'flex min-h-[80px] w-full cursor-pointer items-center justify-between rounded-lg border',
        {
          'cursor-not-allowed border-2 border-bold': isActive,
        },
      )}
      onClick={handleCategorySelect(category.slug)}
    >
      <span className=" relative h-[80px] min-h-[80px] w-[80px] flex-shrink-0 overflow-hidden rounded-l-md">
        {category.isClosed && (
          <span className="absolute left-0 top-0 z-[1] flex h-full w-full items-center justify-center bg-black bg-opacity-60" />
        )}
        {category.image ? (
          <Image
            transform={TransformType.SQUARE}
            image={category.image}
            alt={category.name}
            layout="fill"
            objectFit="cover"
            sizes="60px"
          />
        ) : (
          <DefaultImage />
        )}
      </span>

      <span className="flex-grow px-4 py-2 text-left my-label-md">
        {category.name}
        {category.isClosed && (
          <span className="relative mt-2 block w-fit">
            <MenuBadge>
              <BlockedFilledIcon className="mr-1 h-4 w-4" /> Not available
            </MenuBadge>
          </span>
        )}
        {category.isWaitTimeActive && (
          <span className="relative mt-2 block w-fit">
            <FlexibleWaitTimesBadge
              estimationInMinutes={category.waitTimeEstimationInMinutes}
            />
          </span>
        )}
      </span>

      <ChevronRightIcon className="mr-3 flex-shrink-0" />
    </button>
  )
}

export const CategorySheet = ({
  categorySheetOpen,
  setCategorySheetOpen,
  menuCategories,
  activeCategory,
  handleCategorySelect,
}: CategorySheetProps) => {
  return (
    <ResponsiveModal onClose={() => setCategorySheetOpen(false)}>
      {categorySheetOpen && (
        <Sheet
          isCloseable={true}
          showBack={false}
          onClose={() => setCategorySheetOpen(false)}
          title={<FormattedMessage defaultMessage="Select menu" id="Ak6K2+" />}
        >
          <div className="max-h-[460px] space-y-4 overflow-y-auto p-4">
            {menuCategories.map((category) => (
              <CategorySheetItem
                category={{
                  name: category.name,
                  slug: category.slug,
                  image: category.image ?? undefined,
                  isClosed: category.isClosed,
                  isWaitTimeActive: activeCategory.isWaitTimeActive,
                  waitTimeEstimationInMinutes:
                    activeCategory.waitTimeEstimationInMinutes,
                }}
                isActive={activeCategory.slug === category.slug}
                handleCategorySelect={handleCategorySelect}
                key={category.slug}
              />
            ))}
          </div>
        </Sheet>
      )}
    </ResponsiveModal>
  )
}
